import React, { useState, useEffect, StrictMode } from "react";
import { View, FlatList, StyleSheet, TouchableOpacity } from "react-native";
import OtherListCardCommon from "../../../components/OtherListCard/OtherListCardCommon";
import { faTicket, faRestroom, faQuestion, faGauge, faBuilding, faParking } from "@fortawesome/free-solid-svg-icons";
import { useNavigation } from "@react-navigation/native";
import { formatAddress, parkingFormatAddress } from "../../../utils/formatting";
import AttractionsStore from "../../../storage/attractionsStore";
import ParkingStore from "../../../storage/parkingStore";
import Loading from "../../../components/Loading/Loading";
import NoResults from "../../../components/NoResults/NoResults";
import BathroomsStore from "../../../storage/bathroomsStore";
import { getOpenSeasons } from "../../../utils/helper";
import { getDistancesOfLocationsFromUser, sortDistancesOfLocations } from "../../../utils/geolocation";
import { STRINGS } from "../../../constants";
import useMetaDescriptionOnFocus from "../../../components/Meta/MetaDescription";
import ParkingIndicator from "../../../components/ParkingIndicator/ParkingIndicator";
import ParkingDisclaimer from "../../../components/ParkingDisclaimer/ParkingDisclaimer";

/**
 * ListViewCommon for Parking, Bathrooms, Attractions component computes and sends data to the OtherListCard component.
 *
 * @param {object} props - The component props.
 * @param {Array} props.data - The array of data (parking, attractions, bathrooms, etc.).
 * @param {JSX.Element} props.FontAwesomeIcon - The FontAwesomeIcon component passed down from ListView.
 *
 * @returns {JSX.Element} The ListViewCommon component.
 */
const ListViewCommon = ({ data, FontAwesomeIcon, iconSize, type }) => {
  const [sortedParking, setSortedParking] = useState();
  const [sortedBathrooms, setSortedBathrooms] = useState();
  const [sortedAttractions, setSortedAttractions] = useState();
  const attractions = AttractionsStore((state) => state.attractions);
  const loadingAttractions = AttractionsStore((state) => state.loadingAttractions);
  const fetchAttractions = AttractionsStore((state) => state.fetchAttractions);
  const bathrooms = BathroomsStore((state) => state.bathrooms);
  const loadingBathrooms = BathroomsStore((state) => state.loadingBathrooms);
  const fetchBathrooms = BathroomsStore((state) => state.fetchBathrooms);
  const parking = ParkingStore((state) => state.parking);
  const loadingParking = ParkingStore((state) => state.loadingParking);
  const fetchParking = ParkingStore((state) => state.fetchParking);
  const navigation = useNavigation();
  useMetaDescriptionOnFocus(STRINGS.metaListDescription);

  const parkingTypeIcons = {
    meter: faGauge,
    structure: faBuilding,
    default: faParking,
  };

  useEffect(() => {
    if (type === "parking") {
      setSortedParking(parking);
      const sortParkingByDistanceToUser = async () => {
        if (parking.length > 0) {
          try {
            setSortedParking(sortDistancesOfLocations(await getDistancesOfLocationsFromUser(parking)));
          } catch (err) {
            setSortedParking(parking);
          }
        }
      };

      sortParkingByDistanceToUser();
    }
  }, [parking]);

  useEffect(() => {
    if (type === "bathroom") {
      setSortedBathrooms(bathrooms);
      const getDistanceOfBathroomsFromUser = async () => {
        try {
          if (bathrooms.length > 0) {
            setSortedBathrooms(sortDistancesOfLocations(await getDistancesOfLocationsFromUser(bathrooms)));
          }
        } catch (err) {
          setSortedBathrooms(bathrooms);
        }
      };

      getDistanceOfBathroomsFromUser();
    }
  }, [bathrooms]);

  useEffect(() => {
    if (type === "attraction") {
      setSortedAttractions(attractions);
      const getDistanceOfAttractionsFromUser = async () => {
        try {
          if (attractions.length > 0) {
            setSortedAttractions(sortDistancesOfLocations(await getDistancesOfLocationsFromUser(attractions)));
          }
        } catch (err) {
          setSortedAttractions(attractions);
        }
      };

      getDistanceOfAttractionsFromUser();
    }
  }, [attractions]);

  const renderItem = ({ item, index }) => {
    let icon, chipIcon, chipBackgroundColor, chipBorderColor, chipIconColor;
    let chipText = null;
    let distance;
    let subTitle;
    let parkingColor;
    let parkingText;
    if (type === "parking") {
      distance = item.distance ? `${item.distance} ${STRINGS.milesAway}` : null;
      icon = parkingTypeIcons[item.type] || parkingTypeIcons.default;
      parkingColor = item.statusColor;
      parkingText = item.statusText;
      subTitle = parkingFormatAddress(item.address || "");
    } else if (type === "bathroom") {
      const dist = item.distance;
      icon = faRestroom;
      subTitle = dist != undefined ? `${dist} ${STRINGS.milesAway}` : null;
      ({ chipText, chipIcon, chipBackgroundColor, chipBorderColor, chipIconColor } = getOpenSeasons(item.attributes));
    } else if (type === "attraction") {
      distance = item.distance ? `${item.distance} ${STRINGS.milesAway}` : null;
      icon = faTicket;
      subTitle = formatAddress(item.address, item.city, item.zip);
    } else {
      icon = faQuestion;
    }

    return (
      <TouchableOpacity onPress={() => handleDetailNavigation(item, index)}>
        <OtherListCardCommon
          title={item.name || item.attributes?.Location}
          distance={distance}
          subTitle={subTitle}
          chipText={chipText}
          showChip={type == "bathroom"} // 'type === "parking" || ' removing parking chip for now
          icon={icon} // Icon for left section
          chipIcon={chipIcon} // Icon for the chip
          FontAwesomeIcon={FontAwesomeIcon}
          chipBackgroundColor={chipBackgroundColor}
          chipBorderColor={chipBorderColor}
          chipIconColor={chipIconColor}
          iconSize={iconSize}
          parkingIndicator={
            type === "parking" && <ParkingIndicator parkingColor={parkingColor} parkingText={parkingText} />
          }
        />
      </TouchableOpacity>
    );
  };
  const handleDetailNavigation = (item, index) => {
    if (type == "attraction") {
      const attractionIndex = attractions.findIndex((attractionItem) => attractionItem.name === item.name);
      navigation.navigate("AttractionsDetailsView", { index: attractionIndex });
    } else if (type == "bathroom") {
      const bathroomIndex = bathrooms.findIndex((bathroomItem) => bathroomItem.id === item.id);
      navigation.navigate("BathroomDetailsView", { index: bathroomIndex });
    } else if (type === "parking") {
      const parkingIndex = parking.findIndex((parkingItem) => parkingItem.id === item.id);
      navigation.navigate("ParkingDetailsView", { index: parkingIndex });
    }
  };

  /**
   * Refreshes data based on the specified type.
   * Depending on the type, it triggers the corresponding store's fetch method
   * to retrieve updated data for attractions, bathrooms, or parking.
   */
  const refresh = () => {
    if (type === "attraction") {
      fetchAttractions();
    } else if (type === "bathroom") {
      fetchBathrooms();
    } else if (type === "parking") {
      fetchParking();
    }
  };

  return (
    <StrictMode>
      <View style={styles.container}>
        {(type === "attraction" && loadingAttractions) ||
        (type === "bathroom" && loadingBathrooms) ||
        (type === "parking" && loadingParking) ? (
          <Loading />
        ) : (type === "attraction" && attractions.length === 0) ||
          (type === "bathroom" && bathrooms.length === 0) ||
          (type === "parking" && parking.length === 0) ? (
          <NoResults refresh={refresh} />
        ) : (
          <FlatList
            data={
              type === "attraction"
                ? sortedAttractions
                : type === "parking"
                ? sortedParking
                : type === "bathroom"
                ? sortedBathrooms
                : data
            }
            renderItem={renderItem}
            keyExtractor={(item, index) => {
              return index.toString();
            }}
            contentContainerStyle={styles.flatListContent}
            // ListHeaderComponent={type === "parking" && <ParkingDisclaimer />}
          />
        )}
      </View>
    </StrictMode>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingHorizontal: 16,
    paddingTop: 16,
    position: "relative",
  },
  flatListContent: {
    flexGrow: 1,
  },
});

export default React.memo(ListViewCommon);
